import { Component, Inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { ClinicalOnboardingService } from '../../services/clinical-onboarding.service';
import { ToastService } from '../../services/general/toast.service';

/**
 * Il gestore registra il pagamento di un clinico, oppure ne corregge importo / data /
 * CRO se `payment` e' valorizzato (modalita' modifica). In registrazione l'importo e'
 * precompilato col compenso netto del clinico (inserito, altrimenti la stima dal
 * regime fiscale) ma resta modificabile. Aperta dal menu azioni di una riga della tabella
 * onboarding, che ha gia' nome e compenso del clinico (niente caricamento del dettaglio).
 */
@Component({
  selector: 'app-modal-register-clinician-payment',
  templateUrl: './modal-register-clinician-payment.component.html',
  styleUrls: ['./modal-register-clinician-payment.component.scss'],
  standalone: false,
})
export class ModalRegisterClinicianPaymentComponent {
  saving = false;
  form: FormGroup;

  constructor(
    public dialogRef: MatDialogRef<ModalRegisterClinicianPaymentComponent>,
    @Inject(MAT_DIALOG_DATA) public data: {
      surveyId: string | number;
      candidateId: number;
      name: string;
      suggestedAmount: number | null;
      payment?: { paid_amount: number | null; payment_date: string | null; cro_reference: string | null };
    },
    private fb: FormBuilder,
    private clinicalOnboardingService: ClinicalOnboardingService,
    private toastService: ToastService,
    private translateService: TranslateService,
  ) {
    const existing = data.payment;
    this.form = this.fb.group({
      paid_amount: [(existing ? existing.paid_amount : data.suggestedAmount) ?? null, [Validators.min(0)]],
      payment_date: [existing?.payment_date ? this.parseDate(existing.payment_date) : new Date(), Validators.required],
      cro_reference: [existing?.cro_reference || ''],
    });
  }

  get isEdit(): boolean {
    return !!this.data.payment;
  }

  /** YYYY-MM-DD -> Date locale (new Date('YYYY-MM-DD') e' UTC e sposterebbe il giorno). */
  private parseDate(value: string): Date {
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  /** YYYY-MM-DD dalle parti locali: toISOString() sposterebbe la data di un giorno vicino alla mezzanotte. */
  private toDateString(date: Date): string {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}-${month}-${day}`;
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.saving = true;
    const value = this.form.value;

    const payload = {
      paid_amount: value.paid_amount ?? null,
      payment_date: this.toDateString(value.payment_date),
      cro_reference: value.cro_reference || null,
    };
    const done = () => {
      this.saving = false;
      this.toastService.success(
        this.translateService.instant('generic.success'),
        this.translateService.instant(this.isEdit ? 'modals.register-clinician-payment.updated' : 'modals.register-clinician-payment.saved'),
      );
      this.dialogRef.close(true);
    };
    const fail = () => {
      this.saving = false;
    };

    if (this.isEdit) {
      this.clinicalOnboardingService.updateClinicianPayment(this.data.surveyId, this.data.candidateId, payload, done, fail);
    } else {
      this.clinicalOnboardingService.registerClinicianPayment(this.data.surveyId, this.data.candidateId, payload, done, fail);
    }
  }

  closeDialogue(): void {
    this.dialogRef.close();
  }
}
